import { useState, useEffect, useCallback } from 'react';

export interface AppSettings {
    fontSize: 'small' | 'medium' | 'large';
    soundEnabled: boolean;
    hapticsEnabled: boolean;
    autoScroll: boolean;
    showTimestamps: boolean;
    sendOnEnter: boolean;
    streamResponses: boolean;
    showAvatars: boolean;
    confirmDelete: boolean;
    autoDeleteDays: 'never' | '7' | '30' | '90';
}

const DEFAULTS: AppSettings = {
    fontSize: 'medium',
    soundEnabled: true,
    hapticsEnabled: true,
    autoScroll: true,
    showTimestamps: false,
    sendOnEnter: true,
    streamResponses: true,
    showAvatars: true,
    confirmDelete: true,
    autoDeleteDays: 'never',
};

const KEYS: Record<keyof AppSettings, string> = {
    fontSize: 'luca_font_size',
    soundEnabled: 'luca_sound_enabled',
    hapticsEnabled: 'luca_haptics_enabled',
    autoScroll: 'luca_auto_scroll',
    showTimestamps: 'luca_show_timestamps',
    sendOnEnter: 'luca_send_on_enter',
    streamResponses: 'luca_stream_responses',
    showAvatars: 'luca_show_avatars',
    confirmDelete: 'luca_confirm_delete',
    autoDeleteDays: 'luca_auto_delete_days',
};

function readSettings(): AppSettings {
    return {
        fontSize: (localStorage.getItem(KEYS.fontSize) as AppSettings['fontSize']) || DEFAULTS.fontSize,
        soundEnabled: localStorage.getItem(KEYS.soundEnabled) !== 'false',
        hapticsEnabled: localStorage.getItem(KEYS.hapticsEnabled) !== 'false',
        autoScroll: localStorage.getItem(KEYS.autoScroll) !== 'false',
        showTimestamps: localStorage.getItem(KEYS.showTimestamps) === 'true',
        sendOnEnter: localStorage.getItem(KEYS.sendOnEnter) !== 'false',
        streamResponses: localStorage.getItem(KEYS.streamResponses) !== 'false',
        showAvatars: localStorage.getItem(KEYS.showAvatars) !== 'false',
        confirmDelete: localStorage.getItem(KEYS.confirmDelete) !== 'false',
        autoDeleteDays: (localStorage.getItem(KEYS.autoDeleteDays) as AppSettings['autoDeleteDays']) || DEFAULTS.autoDeleteDays,
    };
}

export function useSettings(): AppSettings {
    const [settings, setSettings] = useState<AppSettings>(readSettings);

    useEffect(() => {
        const handler = () => {
            setSettings(readSettings());
        };

        window.addEventListener('luca-setting-change', handler);
        return () => window.removeEventListener('luca-setting-change', handler);
    }, []);

    return settings;
}
